/*
 * Home editorial (Miu Miu style) section behaviour.
 *
 * - Header colour follows the panel under it. On desktop nav the switch is applied
 *   at the START of the move (not once it lands), so the colour changes with the scroll.
 * - On a dark slide (light/transparent header), moving the pointer near the top
 *   reveals a solid white header bar; it fades out on leave.
 * - Panel snapping over sticky-stacking panels (the next panel slides up over the pinned one =
 *   the overlap): wheel/keyboard = one notch/key per panel at every width; touch follows the finger and settles
 *   on release using visible area and deliberate flick velocity.
 * - Scroll reveal via IntersectionObserver (disabled under reduced-motion).
 * - Lightbox for the editorial gallery photos.
 *
 * All class names are namespaced with `he-` to avoid clashing with the theme.
 */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  function initSection(root) {
    if (!root || root.dataset.heInit === 'true') return;
    root.dataset.heInit = 'true';

    var header = root.querySelector('.he-header');
    var panels = Array.prototype.slice.call(root.querySelectorAll('.he-panel'));
    if (!header || panels.length === 0) return;

    // ---- Shared header state ----
    // Header colour follows only the section currently behind it.
    var baseSolid = false;
    var suspendNavigation = function () {};
    var resumeNavigation = function () {};
    var navLockUntil = 0; // while performance.now() < this, ignore scroll-driven updates
    var externalOverlayOpen = document.body.classList.contains('llukes-panel-open');
    var closeLightbox = function () {};
    var mobileHeader = window.matchMedia('(max-width: 749px)');
    var headerTransition = null;
    var headerCompactDuration = 520;
    var navigationTarget = null;
    function overlayIsOpen() {
      return externalOverlayOpen || document.body.classList.contains('llukes-panel-open') ||
        document.body.classList.contains('he-lightbox-open');
    }
    document.addEventListener('llukes:overlay', function (event) {
      externalOverlayOpen = Boolean(event.detail && event.detail.open);
      if (externalOverlayOpen) {
        // The shared panel emits before saving body styles, so release our lock first.
        closeLightbox(false);
        suspendNavigation();
      } else {
        resumeNavigation();
      }
      syncMobileHeader();
    });

    function syncMobileHeader() {
      if (!mobileHeader.matches) {
        header.classList.toggle('is-compact', navigationTarget !== null ? navigationTarget > 0 : window.scrollY > 8);
        return;
      }
      if (headerTransition) return;
      var targetRequiresCompactHeader = navigationTarget !== null
        ? navigationTarget > 0
        : window.scrollY > 8;
      header.classList.toggle('is-mobile-compact', externalOverlayOpen || targetRequiresCompactHeader);
    }

    function waitForCompactHeader(trigger) {
      if (!mobileHeader.matches || !header.contains(trigger) || header.classList.contains('is-mobile-compact')) return null;
      header.classList.add('is-mobile-compact', 'is-mobile-transitioning');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        header.classList.remove('is-mobile-transitioning');
        return null;
      }
      return new Promise(function (resolve) {
        headerTransition = window.setTimeout(function () {
          headerTransition = null;
          header.classList.remove('is-mobile-transitioning');
          resolve();
        }, headerCompactDuration);
      });
    }

    window.llukesBeforePanelOpen = function (id, trigger) {
      if (id !== 'llukes-menu' && id !== 'llukes-cart') return null;
      return waitForCompactHeader(trigger);
    };

    function panelIsDark(p) { return p.dataset.dark === 'true'; }
    function applyHeader() { header.classList.toggle('is-solid', baseSolid); }

    // ---- Header theme follows the panel under it ----
    function updateFromScroll() {
      if (overlayIsOpen() || performance.now() < navLockUntil) return; // nav already applied the target state
      var y = 6, topPanel = panels[0];
      // Last panel (in DOM order) covering the very top wins = the one painted on top.
      for (var i = 0; i < panels.length; i++) {
        var r = panels[i].getBoundingClientRect();
        if (r.top <= y && r.bottom > y) topPanel = panels[i];
      }
      header.classList.toggle('is-hero-active', topPanel.classList.contains('he-hero'));
      if (!mobileHeader.matches) header.classList.toggle('is-compact', window.scrollY > 8);
      baseSolid = !panelIsDark(topPanel);
      applyHeader();
    }
    window.addEventListener('scroll', updateFromScroll, { passive: true });
    window.addEventListener('scroll', syncMobileHeader, { passive: true });
    window.addEventListener('resize', updateFromScroll);
    if (mobileHeader.addEventListener) mobileHeader.addEventListener('change', syncMobileHeader);
    else if (mobileHeader.addListener) mobileHeader.addListener(syncMobileHeader);
    updateFromScroll();
    syncMobileHeader();

    // ---- Scroll reveal ----
    (function () {
      var els = root.querySelectorAll('.he-reveal');
      if (!('IntersectionObserver' in window)) {
        els.forEach(function (el) { el.classList.add('is-visible'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
      els.forEach(function (el) { io.observe(el); });
    })();

    // ---- Panel snapping ----
    // Sticky panels provide the overlap; touch controls the document position until release.
    if (root.dataset.wheelNav !== 'false') {
      var locked = false;
      var touch = null;
      var frame = 0;
      var dragFrame = 0;
      var pendingScroll = null;
      var wheelConfig = {
        axisLockDistance: 8,
        verticalIntentRatio: 1.2,
        triggerDistance: 24,
        silenceRearmDelay: 80,
        inversionRearmDistance: 12,
        resumedGestureMinimumDelta: 8,
        resumedGestureInertiaMultiplier: 1.8,
        animationDuration: 420
      };
      var wheelState = {
        phase: 'idle',
        axis: null,
        horizontalDistance: 0,
        verticalDistance: 0,
        verticalDelta: 0,
        direction: 0,
        inversionDistance: 0,
        lastInertiaDelta: 0,
        lastEventAt: 0
      };
      var settleTimer = 0;
      var contactCount = 0;
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      var clamp = function (value, min, max) { return Math.max(min, Math.min(max, value)); };
      var panelH = function () { return panels[0].offsetHeight || window.innerHeight; };
      var viewportH = function () {
        return parseFloat(document.documentElement.style.getPropertyValue('--he-viewport-height')) || window.innerHeight;
      };
      var origin = function () { return root.getBoundingClientRect().top + window.scrollY; };
      var position = function (index) {
        var maxScroll = Math.max(0, document.documentElement.scrollHeight - viewportH());
        return clamp(origin() + index * panelH(), 0, maxScroll);
      };
      var currentIndex = function () { return clamp(Math.round((window.scrollY - origin()) / panelH()), 0, panels.length - 1); };
      var unavailable = function () { return !root.isConnected || overlayIsOpen(); };
      var closingPanel = panels[panels.length - 1];
      function hasLongClosing() {
        return closingPanel.classList.contains('he-closing') && closingPanel.offsetHeight > viewportH() + 1;
      }
      function allowsClosingScroll(direction) {
        if (!hasLongClosing() || locked || touch) return false;
        var start = position(panels.length - 1);
        return window.scrollY > start + 1 || (window.scrollY >= start - 1 && direction > 0);
      }
      var writeScroll = function (y) { window.scrollTo({ top: y, behavior: 'instant' }); };

      function isZoomed() {
        return window.visualViewport && window.visualViewport.scale > 1.01;
      }
      function scheduleSettle() {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          if (unavailable() || locked || contactCount || isZoomed() || document.hidden) return;
          if (touch) release({ type: 'touchcancel' });
          if (locked) return;
          // Toolbar resizing changes every panel offset. Preserve the active chapter
          // only after the viewport is quiet and the current gesture has finished.
          var resized = panelH() !== layoutHeight;
          var closingOffset = window.scrollY - (origin() + (panels.length - 1) * layoutHeight);
          layoutHeight = panelH();
          if (hasLongClosing() && settledIndex === panels.length - 1 && resized && closingOffset > 1) {
            writeScroll(clamp(position(panels.length - 1) + closingOffset, 0,
              document.documentElement.scrollHeight - viewportH()));
            return;
          }
          if (hasLongClosing() && window.scrollY >= position(panels.length - 1) - 1) {
            settledIndex = panels.length - 1;
            return;
          }
          var index = resized ? settledIndex : currentIndex();
          if (Math.abs(window.scrollY - position(index)) > 1) goTo(index);
        }, 180);
      }
      // Event-driven safety net: never interrupt a held gesture or poll while idle.
      window.addEventListener('scroll', scheduleSettle, { passive: true });
      window.addEventListener('scrollend', scheduleSettle, { passive: true });
      window.addEventListener('pageshow', scheduleSettle);
      window.addEventListener('touchstart', function (e) {
        contactCount = e.touches.length;
        clearTimeout(settleTimer);
      }, { passive: true, capture: true });
      function finishContacts(e) {
        contactCount = e.touches.length;
        if (!contactCount) release(e);
        scheduleSettle();
      }
      window.addEventListener('touchend', finishContacts, { passive: true, capture: true });
      window.addEventListener('touchcancel', finishContacts, { passive: true, capture: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', scheduleSettle);
        window.visualViewport.addEventListener('scroll', scheduleSettle);
      }
      window.addEventListener('blur', function () {
        contactCount = 0;
        release({ type: 'touchcancel' });
      });
      window.addEventListener('focus', scheduleSettle);

      function flushDrag() {
        cancelAnimationFrame(dragFrame);
        dragFrame = 0;
        if (pendingScroll !== null) {
          writeScroll(pendingScroll);
          pendingScroll = null;
        }
      }

      suspendNavigation = function () {
        cancelAnimationFrame(frame);
        clearTimeout(settleTimer);
        flushDrag();
        touch = null;
        locked = false;
        navLockUntil = 0;
        resetWheelState();
      };
      resumeNavigation = function () {
        if (unavailable()) return;
        layoutHeight = panelH();
        settledIndex = currentIndex();
        updateFromScroll();
        scheduleSettle();
      };

      var settledIndex = currentIndex();
      var layoutHeight = panelH();

      function goTo(index) {
        if (unavailable()) return;
        index = clamp(index, 0, panels.length - 1);
        settledIndex = index;
        cancelAnimationFrame(frame);
        flushDrag();
        locked = true;
        var start = window.scrollY;
        var target = position(index);
        var started = performance.now();
        var duration = reducedMotion.matches ? 0 : (mobileHeader.matches ? headerCompactDuration : wheelConfig.animationDuration);
        navigationTarget = index;
        syncMobileHeader();
        header.classList.toggle('is-hero-active', panels[index].classList.contains('he-hero'));
        baseSolid = !panelIsDark(panels[index]);
        applyHeader();
        navLockUntil = started + duration;
        function tick(now) {
          if (unavailable()) { locked = false; navigationTarget = null; return; }
          var progress = duration ? Math.min(1, (now - started) / duration) : 1;
          var eased = 1 - Math.pow(1 - progress, 3);
          writeScroll(start + (target - start) * eased);
          if (progress < 1) frame = requestAnimationFrame(tick);
          else {
            locked = false;
            navLockUntil = 0;
            navigationTarget = null;
            syncMobileHeader();
            updateFromScroll();
            scheduleSettle();
          }
        }
        frame = requestAnimationFrame(tick);
      }

      function resetWheelState() {
        wheelState.phase = 'idle';
        wheelState.axis = null;
        wheelState.horizontalDistance = 0;
        wheelState.verticalDistance = 0;
        wheelState.verticalDelta = 0;
        wheelState.direction = 0;
        wheelState.inversionDistance = 0;
        wheelState.lastInertiaDelta = 0;
        wheelState.lastEventAt = 0;
      }

      function startWheelGesture(deltaX, deltaY) {
        wheelState.horizontalDistance += Math.abs(deltaX);
        wheelState.verticalDistance += Math.abs(deltaY);
        wheelState.verticalDelta += deltaY;

        if (!wheelState.axis &&
            wheelState.horizontalDistance + wheelState.verticalDistance >= wheelConfig.axisLockDistance) {
          if (wheelState.verticalDistance >= wheelState.horizontalDistance * wheelConfig.verticalIntentRatio) {
            wheelState.axis = 'vertical';
          } else if (wheelState.horizontalDistance >=
              wheelState.verticalDistance * wheelConfig.verticalIntentRatio) {
            wheelState.axis = 'horizontal';
          }
        }
        if (wheelState.axis !== 'vertical' ||
            Math.abs(wheelState.verticalDelta) < wheelConfig.triggerDistance) return 0;

        wheelState.phase = 'absorbing';
        wheelState.direction = wheelState.verticalDelta > 0 ? 1 : -1;
        wheelState.inversionDistance = 0;
        wheelState.lastInertiaDelta = Math.abs(deltaY);
        return wheelState.direction;
      }

      function rearmFromInversion(direction) {
        var distance = wheelState.inversionDistance;
        resetWheelState();
        wheelState.axis = 'vertical';
        wheelState.horizontalDistance = 0;
        wheelState.verticalDistance = distance;
        wheelState.verticalDelta = direction * distance;
      }

      function absorbWheelDelta(deltaX, deltaY) {
        var magnitude = Math.abs(deltaY);
        var direction = deltaY > 0 ? 1 : -1;
        if (!magnitude) return 0;

        if (direction !== wheelState.direction) {
          wheelState.inversionDistance += magnitude;
          if (wheelState.inversionDistance < wheelConfig.inversionRearmDistance) return 0;
          rearmFromInversion(direction);
          return startWheelGesture(deltaX, 0);
        }

        wheelState.inversionDistance = 0;
        if (!locked &&
            magnitude >= wheelConfig.resumedGestureMinimumDelta &&
            magnitude >= wheelState.lastInertiaDelta * wheelConfig.resumedGestureInertiaMultiplier) {
          resetWheelState();
          return startWheelGesture(deltaX, deltaY);
        }
        wheelState.lastInertiaDelta = magnitude;
        return 0;
      }

      window.addEventListener('wheel', function (e) {
        if (unavailable() || isZoomed() || e.ctrlKey) return;
        var unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? panelH() : 1;
        var deltaX = e.deltaX * unit;
        var deltaY = e.deltaY * unit;
        if (!deltaY || allowsClosingScroll(deltaY)) return;

        var now = performance.now();
        if (!locked && wheelState.lastEventAt &&
            now - wheelState.lastEventAt >= wheelConfig.silenceRearmDelay) resetWheelState();

        if (locked || touch) {
          if (wheelState.phase === 'absorbing') {
            e.preventDefault();
            absorbWheelDelta(deltaX, deltaY);
            wheelState.lastEventAt = now;
          } else if (Math.abs(deltaY) >= Math.abs(deltaX)) {
            e.preventDefault();
          }
          return;
        }

        var direction = wheelState.phase === 'absorbing'
          ? absorbWheelDelta(deltaX, deltaY)
          : startWheelGesture(deltaX, deltaY);
        wheelState.lastEventAt = now;
        if (wheelState.axis !== 'vertical' && wheelState.phase !== 'absorbing') {
          if (!wheelState.axis && Math.abs(deltaY) >= Math.abs(deltaX)) e.preventDefault();
          return;
        }
        e.preventDefault();
        if (direction) goTo(currentIndex() + direction);
      }, { passive: false });

      window.addEventListener('keydown', function (e) {
        if (unavailable() || isZoomed() || e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        if (e.key === ' ' && e.target.closest('button, a, [role="button"]')) return;
        var direction = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 }[e.key];
        if (e.key === ' ') direction = e.shiftKey ? -1 : 1;
        if (!direction && e.key !== 'Home' && e.key !== 'End') return;
        if (e.key !== 'Home' && allowsClosingScroll(e.key === 'End' ? 1 : direction)) return;
        e.preventDefault();
        if (locked || touch || e.repeat) return;
        goTo(e.key === 'Home' ? 0 : e.key === 'End' ? panels.length - 1 : currentIndex() + direction);
      });

      root.addEventListener('touchstart', function (e) {
        if (unavailable() || isZoomed()) return;
        if (e.touches.length !== 1) {
          release({ type: 'touchcancel' });
          return;
        }
        var point = e.touches[0];
        var gallery = e.target.closest('.he-gallery');
        var height = panelH();
        var top = origin();
        var index = clamp(Math.round((window.scrollY - top) / height), 0, panels.length - 1);
        touch = { x: point.clientX, y: point.clientY, index: index,
          gallery: gallery, galleryLeft: gallery ? gallery.scrollLeft : 0,
          galleryWidth: gallery ? gallery.clientWidth : 0,
          galleryMax: gallery ? gallery.scrollWidth - gallery.clientWidth : 0,
          snapStyle: gallery ? gallery.style.scrollSnapType : '', lastX: point.clientX,
          min: top + Math.max(0, index - 1) * height,
          max: top + Math.min(panels.length - 1, index + 1) * height,
          scroll: window.scrollY, axis: null, blocked: locked, moved: false,
          closing: hasLongClosing() && window.scrollY >= position(panels.length - 1) - 1,
          closingStart: position(panels.length - 1),
          samples: [{ y: point.clientY, time: performance.now() }] };
      }, { passive: true, capture: true });

      root.addEventListener('touchmove', function (e) {
        if (!touch || unavailable() || e.touches.length !== 1) return;
        if (touch.blocked || locked) { e.preventDefault(); return; }
        var point = e.touches[0];
        var dx = point.clientX - touch.x;
        var dy = point.clientY - touch.y;
        if (!touch.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 6) {
          touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (touch.axis === 'x' && touch.gallery) {
          e.preventDefault();
          touch.lastX = point.clientX;
          touch.gallery.style.scrollSnapType = 'none';
          touch.gallery.scrollLeft = clamp(touch.galleryLeft - dx, 0, touch.galleryMax);
          return;
        }
        if (touch.axis !== 'y') return;
        // Let the browser scroll a long footer, including newsletter fields and legal links.
        // At its beginning, a downward finger gesture still returns to the previous chapter.
        if (touch.closing && (touch.scroll > touch.closingStart + 1 || dy < 0)) return;
        e.preventDefault();
        touch.moved = true;
        var now = performance.now();
        touch.samples.push({ y: point.clientY, time: now });
        touch.samples = touch.samples.filter(function (sample) { return now - sample.time <= 100; });
        // One gesture can reveal at most one adjacent panel in either direction.
        pendingScroll = clamp(touch.scroll - dy, touch.min, touch.max);
        if (!dragFrame) dragFrame = requestAnimationFrame(flushDrag);
      }, { passive: false, capture: true });

      function release(e) {
        if (!touch) return;
        flushDrag();
        var gesture = touch;
        touch = null;
        if (gesture.gallery && gesture.axis === 'x') {
          var gallery = gesture.gallery;
          var delta = gesture.x - gesture.lastX;
          var photo = Math.round(gesture.galleryLeft / gesture.galleryWidth);
          if (Math.abs(delta) > gesture.galleryWidth * 0.2) photo += delta > 0 ? 1 : -1;
          gallery.style.scrollSnapType = gesture.snapStyle;
          gallery.scrollTo({ left: clamp(photo * gesture.galleryWidth, 0, gesture.galleryMax), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        }
        if (gesture.blocked || locked || !gesture.moved) return;
        var index = currentIndex();
        var displacement = window.scrollY - gesture.scroll;
        var sample = gesture.samples[0];
        var last = gesture.samples[gesture.samples.length - 1];
        var elapsed = last.time - sample.time;
        var recent = performance.now() - last.time <= 80;
        var velocity = recent && elapsed > 0 ? (sample.y - last.y) / elapsed : 0;
        // Slow releases use the majority. A deliberate flick needs both distance and speed.
        if (e.type !== 'touchcancel' && Math.abs(displacement) >= panelH() * 0.12 &&
            Math.abs(velocity) >= 0.65 && velocity * displacement > 0) {
          index = gesture.index + (displacement > 0 ? 1 : -1);
        }
        goTo(index);
      }

      root.addEventListener('click', function (e) {
        if (locked) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      window.addEventListener('resize', scheduleSettle);
    }

    // ---- Carousel arrows (mobile): prev/next photo. Prev hides on the first, next on the last. ----
    (function () {
      var gallery = root.querySelector('.he-gallery');
      var prevBtn = root.querySelector('.he-gallery__prev');
      var nextBtn = root.querySelector('.he-gallery__next');
      if (!gallery || !prevBtn || !nextBtn) return;
      function updateArrows() {
        var atStart = gallery.scrollLeft <= 4;
        var atEnd = gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 4;
        prevBtn.classList.toggle('is-hidden', atStart);
        nextBtn.classList.toggle('is-hidden', atEnd);
      }
      nextBtn.addEventListener('click', function () { gallery.scrollBy({ left: gallery.clientWidth, behavior: 'smooth' }); });
      prevBtn.addEventListener('click', function () { gallery.scrollBy({ left: -gallery.clientWidth, behavior: 'smooth' }); });
      gallery.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    })();

    // ---- Lightbox for the editorial gallery photos ----
    (function () {
      var items = Array.prototype.slice.call(root.querySelectorAll('.he-gallery__item'));
      var lb = root.querySelector('.he-lightbox');
      if (items.length === 0 || !lb) return;
      var lbImg = lb.querySelector('.he-lightbox__img');
      var sources = items.map(function (it) {
        var img = it.querySelector('img');
        return { src: img.getAttribute('src'), alt: img.getAttribute('alt') };
      });
      var current = 0;
      var trigger = null;
      var inertElements = [];
      var closeButton = lb.querySelector('.he-lightbox__close');
      var status = lb.querySelector('.he-lightbox__status');
      var previousPhoto = lb.querySelector('.he-lightbox__prev');
      var nextPhoto = lb.querySelector('.he-lightbox__next');
      function show(i) {
        current = Math.max(0, Math.min(sources.length - 1, i));
        lbImg.src = sources[current].src;
        lbImg.alt = sources[current].alt;
        previousPhoto.hidden = previousPhoto.disabled = current === 0;
        nextPhoto.hidden = nextPhoto.disabled = current === sources.length - 1;
        if (status) status.textContent = window.llukesStrings.photo_count.replace('{{ index }}', current + 1).replace('{{ total }}', sources.length);
        if ((document.activeElement === previousPhoto && previousPhoto.disabled) ||
            (document.activeElement === nextPhoto && nextPhoto.disabled)) closeButton.focus({ preventScroll: true });
      }
      function lockBackground() {
        var branch = lb;
        while (branch && branch !== document.body) {
          var parent = branch.parentElement;
          if (!parent) break;
          Array.prototype.forEach.call(parent.children, function (sibling) {
            if (sibling !== branch) {
              inertElements.push({ element: sibling, inert: sibling.inert });
              sibling.inert = true;
            }
          });
          branch = parent;
        }
      }
      function restoreBackground() {
        inertElements.forEach(function (entry) { entry.element.inert = entry.inert; });
        inertElements = [];
      }
      var scrollLock = null;
      function saveStyles(element, properties) {
        return properties.map(function (property) {
          return { property: property, value: element.style.getPropertyValue(property), priority: element.style.getPropertyPriority(property) };
        });
      }
      function restoreStyles(element, styles) {
        styles.forEach(function (style) {
          if (style.value) element.style.setProperty(style.property, style.value, style.priority);
          else element.style.removeProperty(style.property);
        });
      }
      function open(i, opener) {
        if (externalOverlayOpen || document.body.classList.contains('llukes-panel-open')) return;
        show(i);
        if (scrollLock) return;
        trigger = opener || items[i];
        suspendNavigation();
        var body = document.body;
        var html = document.documentElement;
        var height = panels[0].offsetHeight;
        var top = root.getBoundingClientRect().top + window.scrollY;
        scrollLock = { x: window.scrollX, y: window.scrollY, height: height,
          index: Math.round((window.scrollY - top) / height),
          bodyStyles: saveStyles(body, ['position', 'top', 'left', 'width', 'overflow-x', 'overflow-y']),
          htmlStyles: saveStyles(html, ['overflow-x', 'overflow-y']) };
        body.classList.add('he-lightbox-open');
        html.style.setProperty('overflow-y', 'hidden');
        body.style.setProperty('position', 'fixed');
        body.style.setProperty('top', -scrollLock.y + 'px');
        body.style.setProperty('left', -scrollLock.x + 'px');
        body.style.setProperty('width', '100%');
        body.style.setProperty('overflow-y', 'hidden');
        lb.classList.add('is-open');
        lb.setAttribute('aria-hidden', 'false');
        lockBackground();
        closeButton.focus({ preventScroll: true });
        window.addEventListener('touchmove', blockScroll, { passive: false });
        window.addEventListener('wheel', blockScroll, { passive: false });
      }
      function close(restoreFocus) {
        if (!scrollLock) return;
        var saved = scrollLock;
        scrollLock = null;
        window.removeEventListener('touchmove', blockScroll);
        window.removeEventListener('wheel', blockScroll);
        restoreStyles(document.body, saved.bodyStyles);
        restoreStyles(document.documentElement, saved.htmlStyles);
        var y = saved.y;
        if (panels[0].offsetHeight !== saved.height) {
          y = root.getBoundingClientRect().top + window.scrollY + saved.index * panels[0].offsetHeight;
        }
        window.scrollTo({ left: saved.x, top: y, behavior: 'instant' });
        document.body.classList.remove('he-lightbox-open');
        lb.classList.remove('is-open');
        lb.setAttribute('aria-hidden', 'true');
        restoreBackground();
        if (restoreFocus !== false && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
        trigger = null;
        resumeNavigation();
      }
      function blockScroll(e) {
        if (!scrollLock || !e.cancelable) return;
        // Keep native pinch zoom and panning available while the body stays fixed.
        if (e.ctrlKey || (e.touches && e.touches.length > 1)) return;
        if (window.visualViewport && window.visualViewport.scale > 1.01) return;
        e.preventDefault();
      }
      closeLightbox = close;
      items.forEach(function (it, i) {
        it.addEventListener('click', function () { open(i, it); });
        it.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          open(i, it);
        });
      });
      closeButton.addEventListener('click', close);
      lb.querySelector('.he-lightbox__next').addEventListener('click', function (e) { e.stopPropagation(); show(current + 1); });
      lb.querySelector('.he-lightbox__prev').addEventListener('click', function (e) { e.stopPropagation(); show(current - 1); });
      lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
      document.addEventListener('keydown', function (e) {
        if (!lb.classList.contains('is-open')) return;
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].indexOf(e.key) !== -1 ||
            (e.key === ' ' && !e.target.closest('button'))) e.preventDefault();
        if (e.key === 'Tab') {
          var controls = Array.prototype.filter.call(lb.querySelectorAll('button:not([disabled])'), function (button) {
            return !button.hidden && button.getClientRects().length;
          });
          var first = controls[0] || closeButton;
          var last = controls[controls.length - 1] || closeButton;
          if (!lb.contains(document.activeElement) || (e.shiftKey && document.activeElement === first)) {
            e.preventDefault();
            (e.shiftKey ? last : first).focus({ preventScroll: true });
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus({ preventScroll: true });
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          show(current + (e.key === 'ArrowRight' ? 1 : -1));
        }
      });
    })();
  }

  function initAll() {
    document.querySelectorAll('.home-editorial').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.home-editorial') : null;
    if (root) initSection(root);
  });
})();
